import { useState, useEffect } from "react";
import { checkApplePayCapabilities } from "../applepay/helpers/helpers";

interface ApplePayCapabilitiesState {
  isCapable: boolean;
  supportsApplePay: boolean;
  isLoading: boolean;
  error?: string;
}

/**
 * Custom hook for checking Apple Pay capabilities
 * This hook manages the async capability checking and provides loading states
 *
 * @param enableApplePay - Whether Apple Pay is enabled in app settings
 * @param isKioskMode - Whether the app is running in kiosk mode
 * @returns Object containing capability state and loading status
 */
export const useApplePayCapabilities = (
  applePayMerchantId: string,
  enableApplePay: boolean,
  isKioskMode: boolean
): ApplePayCapabilitiesState => {
  const [capabilities, setCapabilities] = useState<ApplePayCapabilitiesState>({
    isCapable: false,
    supportsApplePay: false,
    isLoading: true
  });

  useEffect(() => {
    const checkCapabilities = async () => {
      if (!enableApplePay || isKioskMode || !applePayMerchantId) {
        setCapabilities((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const result = await checkApplePayCapabilities(applePayMerchantId);

        setCapabilities({
          ...result,
          isLoading: false
        });
      } catch (error) {
        setCapabilities({
          isCapable: false,
          supportsApplePay: false,
          isLoading: false,
          error: error instanceof Error ? error.message : "Unknown error checking Apple Pay capabilities"
        });
      }
    };

    checkCapabilities();
  }, [enableApplePay, isKioskMode]);

  return capabilities;
};
